import {
  PracticeSetupStepCategory,
  PracticeSetupStepId,
} from '@principle-theorem/principle-core/interfaces';
import { asReffable, Firestore } from '@principle-theorem/shared';
import { from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Brand } from '../../brand';
import { Organisation } from '../../organisation/organisation';
import { Practice } from '../practice';
import { PracticeSetupStepValidation } from './practice-setup-step-validation';
import {
  IPageVisitTrackingConfig,
  IPracticeSetupStepDefinition,
  IValidationResult,
} from './practice-setup-step.types';
import { initRawSchema } from '@principle-theorem/editor';

const pageVisitTracking: IPageVisitTrackingConfig = {
  routes: ['settings/workspace/roles'],
  validationMode: 'all',
};

export const roleSettingsStep: IPracticeSetupStepDefinition = {
  id: PracticeSetupStepId.RoleSettings,
  category: PracticeSetupStepCategory.GeneralPracticeSettings,
  title: 'Review and Configure Roles',
  description: initRawSchema(
    `Review default roles, configure permissions, add any additional roles needed. Roles are used to organise your team into groups and assign permissions. By default, Principle includes a few obvious roles configured with what we believe to be the appropriate permissions. Review each of these and add any additional roles you may need.`
  ),
  dependsOn: [],
  getRoute: (_brand, _practice) => `settings/workspace/roles`,
  validate$: (practice$, step, options) => {
    return practice$.pipe(
      switchMap((practice) => {
        if (options?.reviewed || options?.skipped) {
          return from([{ valid: true } as IValidationResult]);
        }

        const brandRef = Practice.brandRef(practice);
        const organisationRef = Brand.organisationRef(asReffable(brandRef));

        return from(
          Firestore.getDocs(Organisation.roleCol({ ref: organisationRef }))
        ).pipe(
          map((roles) => {
            const activeRoles = roles.filter(
              (role) => !role.deleted && role.permissions?.length > 0
            );

            if (activeRoles.length === 0) {
              return {
                valid: false,
                reason: 'No role with permissions has been configured',
              };
            }

            const pageVisitValidation =
              PracticeSetupStepValidation.validatePageVisits(
                step,
                pageVisitTracking,
                activeRoles.length
              );

            if (pageVisitValidation.valid) {
              return { valid: true };
            }

            const pageVisits = step?.pageVisits ?? [];
            const visitedRoleIds = new Set(
              pageVisits
                .filter((visit) => visit.route.includes('roles/'))
                .map((visit) => {
                  const segments = visit.route.split('/');
                  const index = segments.lastIndexOf('roles');
                  return segments[index + 1];
                })
                .filter((id): id is string => !!id)
            );
            const remainingRoles = activeRoles
              .filter((role) => !visitedRoleIds.has(role.ref.id))
              .map((role) => role.name);

            return {
              valid: false,
              reason: `Please review all roles before completing this step (${
                pageVisitValidation.reason
              }). Remaining roles: \r\n${remainingRoles
                .map((name) => `- ${name}`)
                .sort()
                .join('\r\n')}`,
              progress: {
                current: pageVisitValidation.progress?.current || 0,
                total: activeRoles.length,
              },
            };
          })
        );
      })
    );
  },
  pageVisitTracking,
};
